import { QueryClient } from '@tanstack/react-query';

/**
 * Market data changes once a day, so aggressive refetching buys nothing and
 * burns the EODHD quota. The sync jobs are the write path; the UI just reads.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => (isPermanent(error) ? false : failureCount < 2),
      },
    },
  });
}

export const queryKeys = {
  fxRates: ['fx-rates'] as const,
  accounts: ['accounts'] as const,
  categories: ['categories'] as const,
  syncRuns: ['sync-runs'] as const,
  integrations: ['integrations'] as const,
  portfolio: ['portfolio'] as const,
  importAccounts: ['import-accounts'] as const,
  importLookup: ['import-lookup'] as const,
} as const;

/**
 * Errors that will fail identically on a retry.
 *
 * A PostgrestError carries `code` but **not** `status`, so checking only for a
 * 4xx status silently retries every permission and query error three times —
 * three identical 401s on the login screen, and three failed writes whenever a
 * policy says no.
 */
function isPermanent(error: unknown): boolean {
  if (typeof error !== 'object' || error === null) return false;

  const { code, status } = error as { code?: unknown; status?: unknown };

  if (typeof code === 'string') {
    // PGRST300-303 are the JWT family: expired, malformed, or — on a laptop
    // whose Docker VM clock has drifted — "issued at future". supabase-js
    // refreshes the token in the background, so a retry genuinely can succeed.
    if (/^PGRST30\d$/.test(code)) return false;

    // Every other PostgREST code is a bad request, an unknown table or a
    // schema mismatch. Those do not fix themselves.
    if (code.startsWith('PGRST')) return true;
  }

  // SQLSTATE class 42 is syntax and access-rule violations, which includes
  // 42501 "insufficient privilege" — an RLS denial.
  if (typeof code === 'string' && code.startsWith('42')) return true;

  // Other error shapes (Functions, Storage, fetch wrappers) do carry a status.
  if (typeof status === 'number' && status >= 400 && status < 500) return true;

  return false;
}
